import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import apiService from "../services/api";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!email) {
    navigate("/login");
    return null;
  }

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError(t('auth.enter_6_digit'));
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiService.post("/auth/verify-email", { email, code });
      if (response.success) {
        setSuccess(t('auth.email_verified_redirect'));
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setTimeout(() => {
          // Force page reload to ensure AuthContext picks up the new token
          window.location.href = "/dashboard";
        }, 1500);
      }
    } catch (err) {
      setError(err.message || t('auth.invalid_or_expired_code'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiService.post("/auth/resend-verification", { email });
      if (response.success) {
        setSuccess(t('auth.new_code_sent'));
      }
    } catch (err) {
      setError(err.message || t('auth.resend_error'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-20 font-inter relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-full max-w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-full max-w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="w-full max-w-full max-w-[450px] z-10 animate-fade-in">
        <div className="glass-card p-10 border-white/10 shadow-2xl relative bg-slate-900/60 backdrop-blur-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center border border-indigo-500/20">
              <Mail size={32} className="text-indigo-400" />
            </div>
          </div>
          
          <h2 className="text-2xl font-black text-white font-outfit mb-4 text-center uppercase tracking-tight">{t('auth.email_verification')}</h2>
          <p className="text-slate-400 font-medium mb-8 text-center text-sm">
            {t('auth.enter_security_code')} <strong className="text-indigo-400">{email}</strong>
          </p>

          {error && (
            <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-3 text-rose-400 font-semibold text-sm">
              <div className="w-2 h-2 rounded-full bg-rose-500"></div>
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400 font-semibold text-sm">
              <CheckCircle2 size={16} />
              {success}
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-2">
              <input 
                type="text" 
                value={code} 
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-4 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all font-bold text-center text-2xl tracking-widest"
                disabled={loading}
                maxLength="6"
              />
            </div>

            <button 
              onClick={handleVerify} 
              disabled={loading || code.length !== 6}
              className="btn-premium w-full !py-4 text-white uppercase tracking-widest font-black text-sm flex items-center justify-center gap-3 disabled:opacity-50 group hover:shadow-indigo-500/40"
            >
              {loading ? (
                <Loader2 size={24} className="animate-spin" />
              ) : (
                <>
                  Vérifier
                  <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>

            <div className="pt-4 border-t border-white/5 text-center">
              <p className="text-sm font-medium text-slate-500">
                {t('auth.didnt_receive_code')}{" "}
                <button 
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="text-indigo-400 hover:text-indigo-300 transition-colors font-bold disabled:opacity-50"
                >
                  {resendLoading ? "Envoi..." : "Renvoyer"}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
